/**
 * Cliente HTTP centralizado para la API FastAPI
 * Reemplaza accesos directos a Supabase, TomTom y Open-Meteo
 */

import * as Sentry from '@sentry/react';
import { supabase } from './supabaseClient';
import { Direction } from '../types';
import { logger } from './logger';

const API_URL = import.meta.env.VITE_API_URL;

if (!API_URL) {
  const msg = 'VITE_API_URL no está configurada. La aplicación no puede conectarse a la API.';
  logger.error(msg);
  Sentry.captureMessage(msg, 'fatal');
}

// ==================== Direction Helper ====================

/**
 * Convierte el enum Direction del frontend al formato kebab-case de la API
 */
export function directionToApi(direction: Direction): 'santiago-litoral' | 'litoral-santiago' {
  if (direction === Direction.SANTIAGO_LITORAL) {
    return 'santiago-litoral';
  }
  return 'litoral-santiago';
}

export function directionFromApi(
  direction: string
): Direction {
  if (direction === 'litoral-santiago') {
    return Direction.LITORAL_SANTIAGO;
  }
  return Direction.SANTIAGO_LITORAL;
}

// ==================== API Client ====================

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Platform': 'web',
    };

    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    return headers;
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const fullUrl = `${this.baseUrl}${endpoint}`;
    const url = new URL(fullUrl);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value);
        }
      });
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    return this.handleResponse<T>(response, 'GET', endpoint);
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response, 'POST', endpoint);
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: await this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response, 'PATCH', endpoint);
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: await this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response, 'PUT', endpoint);
  }

  async delete(endpoint: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      await this.handleError(response, 'DELETE', endpoint);
    }
  }

  private async handleResponse<T>(response: Response, method: string, endpoint: string): Promise<T> {
    if (!response.ok) {
      await this.handleError(response, method, endpoint);
    }
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`API respondió con content-type inesperado: ${contentType}. Body: ${text.slice(0, 200)}`);
    }
    return response.json();
  }

  private async handleError(response: Response, method: string, endpoint: string): Promise<never> {
    let errorMessage = `API Error: ${response.status}`;

    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorData.message || errorMessage;
    } catch {
      // Si no se puede parsear el body, usar mensaje por defecto
    }

    const errorContext = {
      method,
      endpoint,
      status: response.status,
      statusText: response.statusText,
    };

    const isWeatherForecastEndpoint = endpoint.includes('/api/v1/weather/forecast');

    if (response.status === 401) {
      logger.warn('Sesión expirada o token inválido', errorContext);
      await supabase.auth.signOut();
    } else if (isWeatherForecastEndpoint && (response.status >= 500 || response.status === 422)) {
      // El cliente tiene fallback local para clima; no elevar a Sentry para evitar ruido.
      logger.warn('Weather API temporalmente no disponible, aplicando fallback', errorContext);
    } else {
      logger.error(errorMessage, new Error(errorMessage), errorContext);
      Sentry.captureException(new Error(errorMessage), {
        tags: { apiEndpoint: endpoint, httpMethod: method, httpStatus: response.status },
      });
    }

    throw new Error(errorMessage);
  }
}

const api = new ApiClient(API_URL);

// ==================== Types ====================

export interface TripResponse {
  id: string;
  routeId: string;
  direction: string;
  travelDate: string;
  scheduledTime: string;
  notificationsEnabled: boolean;
  createdAt: string;
  userId: string | null;
  estimatedDurationMinutes: number | null;
  trafficLevel: string | null;
}

export interface TripListResponse {
  items: TripResponse[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface TripSummaryResponse {
  total: number;
  byRoute: {
    route68: number;
    route78: number;
  };
  byDirection: {
    santiagoLitoral: number;
    litoralSantiago: number;
  };
}

export interface CreateTripRequest {
  routeId: string;
  direction: string;
  travelDate: string;
  scheduledTime: string;
  notificationsEnabled: boolean;
}

export type PredictionModel = 'current' | 'seasonal_rules_v1';
export type DisplayLevel = 'low' | 'medium' | 'high' | 'critical';

export interface TrafficHourPoint {
  hour: number;
  halfHour: 0 | 30;
  level: number;
  estimatedMinutes: number;
  estimatedMinutesOptimistic?: number | null;
  estimatedMinutesPessimistic?: number | null;
  displayLevel?: DisplayLevel | null;
  registeredCount?: number;
  dataConfidence?: 'cold' | 'growing' | 'mature' | null;
}

export interface PredictionMeta {
  sources: string[];
  model?: PredictionModel | null;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  confidence_reason: string | null;
  date_type: string;
  date_type_label: string;
  context_message: string | null;
  dominant_event?: string | null;
  season_type?: string | null;
  data_policy?: 'historical_observations' | 'rules_only' | null;
  smoothing?: 'weighted_3_slot' | 'weighted_5_slot' | null;
  explanation?: string[] | null;
}

export interface TrafficDistributionResponse {
  routeId: string;
  direction: string;
  date: string;
  distribution: TrafficHourPoint[];
  prediction_meta?: PredictionMeta | null;
}

export interface TrafficFeedbackRequest {
  trip_id: string;
  rating: -1 | 0 | 1;
  actual_duration_min?: number;
}

export interface TrafficFeedbackResponse {
  id: string;
  trip_id: string;
  rating: number;
  actual_duration_min: number | null;
  created_at: string;
}

export interface EtaRange {
  best_guess_min: number;
  optimistic_min: number;
  pessimistic_min: number;
  range_label: string;
}

export interface EtaRecommendation {
  best_window: string;
  avoid: string;
}

export interface EtaResponse {
  route_id: string;
  direction: string;
  departure_time: string;
  eta: EtaRange;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  confidence_reason: string | null;
  date_type: string;
  date_type_label: string;
  context_message: string | null;
  recommendation: EtaRecommendation | null;
  realtime_provider: 'here' | 'tomtom' | null;
  data_updated_at: string | null;
}

export interface TrafficCurrentResponse {
  routeId: string;
  direction: string;
  hour: number;
  level: number;
  trafficLevel: 'LIGHT' | 'MODERATE' | 'HEAVY' | 'SEVERE' | 'UNKNOWN';
  estimatedMinutes: number;
  timestamp: string;
  realtime_provider: 'here' | 'tomtom' | null;
}

export interface DirectionsResponse {
  status: 'OK' | 'ERROR' | 'INVALID_ROUTE';
  durationMinutes: number | null;
  distanceKm: number | null;
  trafficLevel: 'LIGHT' | 'MODERATE' | 'HEAVY' | 'SEVERE' | null;
}

export interface WeatherDayForecast {
  date: string;
  maxTemp: number;
  minTemp: number;
  avgTemp: number;
  condition: 'sunny' | 'cloudy' | 'rainy';
  hourly: Array<{
    hour: number;
    temp: number;
    precipProb: number;
    weatherCode: number;
  }>;
}

export interface WeatherForecastResponse {
  latitude: number;
  longitude: number;
  days: WeatherDayForecast[];
}

export interface TripCountResponse {
  count: number;
  routeId: string;
  direction: string;
  date: string;
}

export interface HistoryItem {
  executionId: string;
  tripId: string;
  routeId: string;
  direction: string;
  travelDate: string;
  scheduledTime: string;
  status: 'in_progress' | 'completed' | 'partial' | 'not_done' | 'unknown';
  mode: 'geolocated' | 'non_geolocated';
  estimatedDurationMinutes: number | null;
  trafficLevel: string | null;
  startedAt: string | null;
  endedAt: string | null;
}

export interface HistoryListResponse {
  items: HistoryItem[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface HistoryTimelineEvent {
  name: string;
  timestamp: string;
  status: string | null;
}

export interface HistoryDetailResponse {
  executionId: string;
  tripId: string;
  routeId: string;
  direction: string;
  travelDate: string;
  scheduledTime: string;
  mode: 'geolocated' | 'non_geolocated';
  status: 'in_progress' | 'completed' | 'partial' | 'not_done' | 'unknown';
  estimatedDurationMinutes: number | null;
  trafficLevel: string | null;
  timeline: HistoryTimelineEvent[];
  didTrip: boolean | null;
  estimateAccurate: boolean | null;
  comment: string | null;
}

export interface TripExecutionFeedbackRequest {
  didTrip: boolean;
  estimateAccurate?: boolean;
  comment?: string;
}

export interface TripExecutionFeedbackResponse {
  tripId: string;
  didTrip: boolean | null;
  estimateAccurate: boolean | null;
  comment: string | null;
  submittedAt: string | null;
}

// ==================== API Functions ====================

export const tripsApi = {
  list: (params?: {
    limit?: number;
    offset?: number;
    type?: 'all' | 'upcoming' | 'past';
    filter?: 'upcoming' | 'past';
    routeId?: string;
    fromDate?: string;
    toDate?: string;
    sort?: 'travel_date_desc' | 'travel_date_asc';
  }) =>
    api.get<TripListResponse>('/api/v1/trips', params ? {
      ...(params.limit !== undefined && { limit: params.limit.toString() }),
      ...(params.offset !== undefined && { offset: params.offset.toString() }),
      ...(params.type !== undefined && { type: params.type }),
      ...(params.filter !== undefined && { filter: params.filter }),
      ...(params.routeId !== undefined && { routeId: params.routeId }),
      ...(params.fromDate !== undefined && { fromDate: params.fromDate }),
      ...(params.toDate !== undefined && { toDate: params.toDate }),
      ...(params.sort !== undefined && { sort: params.sort }),
    } : undefined),

  summary: (params?: {
    type?: 'all' | 'upcoming' | 'past';
    filter?: 'upcoming' | 'past';
    routeId?: string;
    direction?: 'santiago-litoral' | 'litoral-santiago';
    fromDate?: string;
    toDate?: string;
  }) =>
    api.get<TripSummaryResponse>('/api/v1/trips/summary', params ? {
      ...(params.type !== undefined && { type: params.type }),
      ...(params.filter !== undefined && { filter: params.filter }),
      ...(params.routeId !== undefined && { routeId: params.routeId }),
      ...(params.direction !== undefined && { direction: params.direction }),
      ...(params.fromDate !== undefined && { fromDate: params.fromDate }),
      ...(params.toDate !== undefined && { toDate: params.toDate }),
    } : undefined),

  create: (data: CreateTripRequest) =>
    api.post<TripResponse>('/api/v1/trips', data),

  get: (id: string) =>
    api.get<TripResponse>(`/api/v1/trips/${id}`),

  update: (id: string, data: Partial<CreateTripRequest>) =>
    api.patch<TripResponse>(`/api/v1/trips/${id}`, data),

  delete: (id: string) =>
    api.delete(`/api/v1/trips/${id}`),

  count: (routeId: string, direction: string, date: string) =>
    api.get<TripCountResponse>('/api/v1/trips/count', {
      routeId,
      direction,
      date,
    }),
};

export const historyApi = {
  list: (params?: {
    limit?: number;
    offset?: number;
    fromDate?: string;
    toDate?: string;
  }) =>
    api.get<HistoryListResponse>('/api/v1/history', params ? {
      ...(params.limit !== undefined && { limit: params.limit.toString() }),
      ...(params.offset !== undefined && { offset: params.offset.toString() }),
      ...(params.fromDate !== undefined && { fromDate: params.fromDate }),
      ...(params.toDate !== undefined && { toDate: params.toDate }),
    } : undefined),

  getDetail: (executionId: string) =>
    api.get<HistoryDetailResponse>(`/api/v1/history/${executionId}`),

  getTripFeedback: (tripId: string) =>
    api.get<TripExecutionFeedbackResponse>(`/api/v1/history/trips/${tripId}/feedback`),

  saveTripFeedback: (tripId: string, data: TripExecutionFeedbackRequest) =>
    api.post<TripExecutionFeedbackResponse>(`/api/v1/history/trips/${tripId}/feedback`, data),
};

export const trafficApi = {
  getDistribution: async (
    routeId: string,
    direction: string,
    date: string,
    predictionModel: PredictionModel = 'seasonal_rules_v1'
  ) => {
    try {
      return await api.get<TrafficDistributionResponse>('/api/v1/traffic/distribution', {
        routeId,
        direction,
        date,
        predictionModel,
      });
    } catch (error) {
      if (predictionModel !== 'seasonal_rules_v1') {
        throw error;
      }
      logger.warn('Traffic API no soporta predictionModel, reintentando modelo current', {
        routeId,
        direction,
        date,
      });
      return api.get<TrafficDistributionResponse>('/api/v1/traffic/distribution', {
        routeId,
        direction,
        date,
      });
    }
  },

  getCurrent: (routeId: string, direction: string) =>
    api.get<TrafficCurrentResponse>('/api/v1/traffic/current', {
      routeId,
      direction,
    }),

  postFeedback: (data: TrafficFeedbackRequest) =>
    api.post<TrafficFeedbackResponse>('/api/v1/traffic/feedback', data),

  getEta: (routeId: string, direction: string, departureTime: string) =>
    api.get<EtaResponse>('/api/v1/traffic/eta', { routeId, direction, departureTime }),
};

export const directionsApi = {
  get: (routeId: string, direction: string, departureTime?: number) =>
    api.get<DirectionsResponse>('/api/v1/directions', {
      routeId,
      direction,
      ...(departureTime !== undefined && { departureTime: departureTime.toString() }),
    }),
};

export const weatherApi = {
  getForecast: (latitude: number, longitude: number, days?: number, date?: string) =>
    api.get<WeatherForecastResponse>('/api/v1/weather/forecast', {
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      ...(days !== undefined && { days: days.toString() }),
      ...(date !== undefined && { date }),
    }),
};

// ==================== Vehicle API ====================

export interface VehicleProfile {
  vehicle_type: string | null;
  fuel_type: string | null;
}

export interface VehicleProfileUpdate {
  vehicle_type?: string | null;
  fuel_type?: string | null;
}

export const vehicleApi = {
  get: () => api.get<VehicleProfile>('/api/v1/profile/vehicle'),
  update: (data: VehicleProfileUpdate) => api.put<VehicleProfile>('/api/v1/profile/vehicle', data),
};
